import ProductSearch from "../components/ProductSearch";
import { useState, useEffect, useCallback } from "react";
import PaginationBar from "../components/PaginationBar";
import { useTheme } from "../context/ThemeContext";
import {
  useLanguage,
  getLang,
  getLocale,
  formatMoney,
  getCurrencySymbol
} from "../lang/useLanguage";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const hdr = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const money = (v) => formatMoney(v);

const getSeverityLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    minor: n.severityMinor,
    major: n.severityMajor,
    critical: n.severityCritical,
  };
};
const SEVERITY_COLORS = { minor: "#22c55e", major: "#f97316", critical: "#ef4444" };
const getStatusLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    open: n.statusOpen,
    under_review: n.statusUnderReview,
    pending_approval: n.statusPendingApproval,
    approved: n.statusApproved,
    closed: n.statusClosed,
    cancelled: n.statusCancelled,
  };
};
const STATUS_COLORS = { open: "#ef4444", under_review: "#f97316", pending_approval: "#eab308", approved: "#60a5fa", closed: "#22c55e", cancelled: "#6b7280" };
const getDispositionLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    rework: n.dispositionRework,
    re_manufacture: n.dispositionRemanufacture,
    replacement: n.dispositionReplacement,
    scrap: n.dispositionScrap,
    use_as_is: n.dispositionUseAsIs,
    return_supplier: n.dispositionReturnSupplier,
    pending: n.dispositionPending,
  };
};
const DISPOSITION_COLORS = { rework: "#60a5fa", re_manufacture: "#a78bfa", replacement: "#f59e0b", scrap: "#ef4444", use_as_is: "#22c55e", return_supplier: "#f97316", pending: "#6b7280" };
const getCategoryLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    dimensional: n.categoryDimensional,
    visual: n.categoryVisual,
    functional: n.categoryFunctional,
    material: n.categoryMaterial,
    process: n.categoryProcess,
    documentation: n.categoryDocumentation,
    assembly: n.categoryAssembly,
    supplier: n.categorySupplier,
    welding: n.categoryWelding,
    production: n.categoryProduction,
    quality: n.categoryQuality,
    customer: n.categoryCustomer,
    other: n.categoryOther,
  };
};
const getCapaStatusLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    open: n.capaStatusOpen,
    in_progress: n.capaStatusInProgress,
    completed: n.capaStatusCompleted,
    verified: n.capaStatusVerified,
    closed: n.capaStatusClosed,
  };
};
const CAPA_STATUS_COLORS = { open: "#ef4444", in_progress: "#f97316", completed: "#60a5fa", verified: "#a78bfa", closed: "#22c55e" };
const getPriorityLabels = () => {
  const n = getLang().ncrPage || {};
  return {
    low: n.priorityLow,
    medium: n.priorityMedium,
    high: n.priorityHigh,
    critical: n.priorityCritical,
  };
};
const PRIORITY_COLORS = { low: "#22c55e", medium: "#f97316", high: "#ef4444", critical: "#7f1d1d" };

const Badge = ({ label, color }) => (
  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color, background: `${color}18`, border: `1px solid ${color}44` }}>{label}</span>
);

const KpiCard = ({ label, value, icon, color, sub, t }) => (
  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: color || t.accent.blue }}>{value ?? "—"}</div>
        {sub && <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 26, opacity: 0.7 }}>{icon}</span>
    </div>
  </div>
);

// ─── MÜŞTERİ İADE FORMU ──────────────────────────────────────────────────
const CustomerReturnModal = ({ onClose, onSaved, t }) => {
  const { t: langPack } = useLanguage();
  const n = langPack.ncrPage || {};
  const [form, setForm] = useState({
    product_code: "", product_name: "", customer_name: "", order_no: "",
    quantity: "", return_reason: "", severity: "major",
    return_type: "refund",
    return_date: new Date().toISOString().slice(0, 10),
    unit_cost: 0, unit_price: 0,
  });
  const [, setAutoPrice]              = useState({ unit_cost: 0, unit_price: 0 });
  const [customerSugg, setCustomerSugg]     = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [orderItems, setOrderItems]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Müşteri adı girilince siparişleri getir
  const fetchCustomerOrders = async (name) => {
    set("customer_name", name);
    set("order_no", ""); set("product_code", ""); set("product_name", "");
    set("max_quantity", 0);
    setOrderItems([]); setCustomerOrders([]);
    if (name.length < 2) { setCustomerSugg([]); return; }
    try {
      const r = await fetch(`${API}/returns/customer-suggest?q=${encodeURIComponent(name)}`, { headers: hdr() });
      if (r.ok) setCustomerSugg(await r.json());
    } catch {}
};

const pickCustomer = async (name) => {
    set("customer_name", name);
    setCustomerSugg([]);
    try {
      const r = await fetch(`${API}/returns/customer-orders?customer_name=${encodeURIComponent(name)}`, { headers: hdr() });
      if (r.ok) setCustomerOrders(await r.json());
    } catch {}
};

  // Sipariş seçilince kalemleri getir
  const fetchOrderItems = async (order_no) => {
    set("order_no", order_no);
    set("product_code", ""); set("product_name", "");
    setAutoPrice({ unit_cost: 0, unit_price: 0 });
    if (!order_no) { setOrderItems([]); return; }
    try {
      const r = await fetch(`${API}/returns/order-items?order_no=${encodeURIComponent(order_no)}`, { headers: hdr() });
      if (r.ok) setOrderItems(await r.json());
    } catch {}
  };

  // Ürün seçilince fiyatları doldur
  const selectItem = (item) => {
    set("product_code", item.product_code);
    set("product_name", item.product_name || "");
    set("unit_price", item.sale_price || 0);
    set("unit_cost", item.unit_cost || 0);
    set("max_quantity", item.quantity || 0);
    setAutoPrice({ unit_cost: item.unit_cost || 0, unit_price: item.sale_price || 0 });
};

  const handleSave = async () => {
    if (!form.product_code) return setError(n.returnProductRequired);
    if (!form.customer_name) return setError(n.returnCustomerRequired);
    if (!form.quantity || form.quantity <= 0) return setError(n.returnQuantityRequired);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/returns`, { method: "POST", headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || n.returnSaveFailed);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;

return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:620, maxHeight:"90vh", overflow:"auto", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:"20px 28px", background:"linear-gradient(135deg,#f9731622,#ef444411)", borderBottom:"1px solid #f9731633", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:1 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>📦 {n.returnTitle}</div>
            <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{n.returnSubtitle}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        <div style={{ padding:28, display:"flex", flexDirection:"column", gap:16 }}>

          {/* İade Tipi */}
          <div><Lbl>{n.returnType} *</Lbl>
            <div style={{ display:"flex", gap:8 }}>
              {[
                  ["refund", `💰 ${n.returnRefund}`],
                  ["replacement", `🔄 ${n.returnReplacement}`]
                ].map(([v,label]) => (
                <button key={v} type="button" onClick={() => set("return_type", v)} style={{
                  flex:1, padding:"9px 0", borderRadius:8, cursor:"pointer",
                  border:`2px solid ${form.return_type===v ? "#fb923c" : t.border.input}`,
                  background: form.return_type===v ? "#fb923c18" : t.bg.input,
                  color: form.return_type===v ? "#fb923c" : t.text.muted,
                  fontSize:12, fontWeight:700
                }}>{label}</button>
              ))}
            </div>
          </div>

          {/* Müşteri */}
          <div style={{ position:"relative" }}><Lbl>{n.returnCustomerName} *</Lbl>
            <input value={form.customer_name}
              onChange={e => fetchCustomerOrders(e.target.value)}
              onBlur={() => setTimeout(() => setCustomerSugg([]), 180)}
              placeholder={n.returnCustomerPlaceholder} style={inp} autoComplete="off" />
            {customerSugg.length > 0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:999,
                background:t.bg.modal, border:`1px solid ${t.border.default}`,
                borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.3)", overflow:"hidden", marginTop:2 }}>
                {customerSugg.map((name, i) => (
                  <div key={i} onMouseDown={() => pickCustomer(name)}
                    style={{ padding:"10px 14px", cursor:"pointer", fontSize:13,
                      color:t.text.primary, borderBottom:`1px solid ${t.border.subtle}`,
                      background:"transparent" }}
                    onMouseEnter={e => e.currentTarget.style.background = t.bg.toggle}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    👤 {name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sipariş Seç */}
          {customerOrders.length > 0 && (
            <div><Lbl>{n.returnSelectOrder}</Lbl>
              <select value={form.order_no} onChange={e => fetchOrderItems(e.target.value)} style={sel}>
                <option value="">— {n.returnSelectOrderPlaceholder} —</option>
                {customerOrders.map(o => (
                  <option key={o.order_no} value={o.order_no}>
                    {o.order_no} — {o.order_date} — {formatMoney(o.total_amount || 0)} ({o.status})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Ürün Seç */}
          {orderItems.length > 0 && (
            <div><Lbl>{n.returnSelectProduct}</Lbl>
              <select onChange={e => { const item = orderItems.find(i=>i.product_code===e.target.value); if(item) selectItem(item); }} style={sel} value={form.product_code}>
                <option value="">— {n.returnSelectProductPlaceholder} —</option>
                {orderItems.map(i => (
                  <option key={i.product_code} value={i.product_code}>
                    {i.product_code} — {i.product_name} — {i.quantity} {n.piece} — {formatMoney(i.sale_price || 0)}/{n.piece}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Miktar + Tarih */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><Lbl>
                {n.returnQuantity} *
                {form.max_quantity > 0 && (
                  <span style={{color:"#fb923c", fontWeight:700}}>
                    / {form.max_quantity} {n.piecesSold}
                  </span>
                )}
              </Lbl>
              <input type="number" min="1" max={form.max_quantity || undefined} value={form.quantity} onChange={e=>set("quantity",e.target.value)} placeholder="0" style={inp} />
            </div>
            <div><Lbl>{n.returnDate}</Lbl>
              <input type="date" value={form.return_date} onChange={e=>set("return_date",e.target.value)} style={inp} />
            </div>
          </div>

          {/* Şiddet */}
          <div><Lbl>{n.severity}</Lbl>
            <select value={form.severity} onChange={e=>set("severity",e.target.value)} style={{ ...sel, color:SEVERITY_COLORS[form.severity], fontWeight:700 }}>
              {Object.entries(getSeverityLabels()).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {/* İade Nedeni */}
          <div><Lbl>{n.returnReason}</Lbl>
            <textarea value={form.return_reason} onChange={e=>set("return_reason",e.target.value)}
              placeholder={n.returnReasonPlaceholder} rows={3}
              style={{ ...inp, resize:"vertical", fontFamily:"inherit" }} />
          </div>

          {error && <div style={{ padding:"10px 14px", background:t.accent.redBg, border:`1px solid ${t.accent.red}44`, borderRadius:8, color:t.accent.red, fontSize:13 }}>⚠️ {error}</div>}

        </div>

        <div style={{ padding:"16px 28px", borderTop:`1px solid ${t.border.default}`, display:"flex", justifyContent:"flex-end", gap:12, background:t.bg.header }}>
          <button onClick={onClose} style={{ padding:"10px 20px", borderRadius:8, border:`1px solid ${t.border.input}`, background:"none", color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{n.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:loading ? t.bg.tag : "linear-gradient(135deg,#f97316,#ef4444)", color:"#fff", cursor:loading?"default":"pointer", fontSize:13, fontWeight:700 }}>
            {loading
              ? (n.saving)
              : `📦 ${n.createReturnRecord}`
            }
          </button>
        </div>

      </div>
    </div>
  );
};

// ─── NCR FORM MODAL ───────────────────────────────────────────────────────
const NCRFormModal = ({ record, onClose, onSaved, t }) => {
  const { t: langPack } = useLanguage();
  const n = langPack.ncrPage || {};

  const empty = {
    source_type: "quality_check", work_order_no: "", sales_order_no: "", product_code: "", product_name: "",
    customer: "", detection_date: new Date().toISOString().slice(0, 10),
    department: "", description: "", quantity_affected: "",
    severity: "minor", ncr_category: "other", disposition: "pending",
    root_cause: "", immediate_action: "", cost_impact: "", notes: "",
  };
  const [form, setForm] = useState(record ? { ...empty, ...record } : empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.product_code) return setError(n.ncrProductRequired);
    if (!form.description) return setError(n.ncrDescriptionRequired);
    if (!form.quantity_affected || parseInt(form.quantity_affected) <= 0) return setError(n.ncrQuantityRequired);
    if (!form.department) return setError(n.ncrDepartmentRequired);
    if (!form.customer) return setError(n.ncrCustomerRequired);
    setLoading(true); setError("");
    try {
      const url = record ? `${API}/ncr/${record.id}` : `${API}/ncr`;
      const method = record ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || n.ncrSaveFailed);
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const Sec = ({ children, color = "#ef4444" }) => <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4, paddingTop: 8, borderTop: `1px solid ${t.border.subtle}` }}>{children}</div>;
  const sevColor = SEVERITY_COLORS[form.severity] || "#6b7280";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 720, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: `linear-gradient(135deg,${sevColor}22,${sevColor}08)`, borderBottom: `1px solid ${sevColor}33`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{record
      ? `✏️ ${n.editNcr}`
      : `🔴 ${n.newNcrTitle}`
    }</div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>{n.newNcrSubtitle}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <Sec color="#ef4444">📋 {n.basicInformation}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{n.sourceType}</Lbl>
              <select value={form.source_type} onChange={e => set("source_type", e.target.value)} style={sel}>
                <option value="quality_check">{n.sourceQualityCheck}</option>
                <option value="customer_complaint">{n.sourceCustomerComplaint}</option>
                <option value="supplier">{n.sourceSupplier}</option>
                <option value="internal_audit">{n.sourceInternalAudit}</option>
                <option value="other">{n.sourceOther}</option>
              </select>
            </div>
            <div><Lbl>{n.detectionDate}</Lbl><input type="date" value={form.detection_date} onChange={e => set("detection_date", e.target.value)} style={inp} /></div>
            <div><Lbl>{n.department}</Lbl><input value={form.department} onChange={e => set("department", e.target.value)} placeholder={n.departmentPlaceholder} style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{n.productCode} *</Lbl>
              <ProductSearch value={form.product_code} onChange={(code, name) => { set("product_code", code); if (name) set("product_name", name); }} placeholder={n.productCodePlaceholder} t={t} />
            </div>
            <div><Lbl>{n.workOrderNo}</Lbl>
              <input value={form.work_order_no} onChange={async e => {
                set("work_order_no", e.target.value);
                if (e.target.value.length > 5) {
                  try {
                    const res = await fetch(`${API}/work-orders?search=${e.target.value}`, { headers: hdr() });
                    if (res.ok) {
                      const data = await res.json();
                      const wo = data.find(w => w.order_no === e.target.value);
                      if (wo) {
                        if (wo.customer) set("customer", wo.customer);
                        if (wo.product_code) set("product_code", wo.product_code);
                        if (wo.product_name) set("product_name", wo.product_name);
                      }
                    }
                  } catch(e) {}
                }
              }} placeholder="IE-2026-0001" style={inp} />
            </div>
            <div><Lbl>{n.salesOrderNo}</Lbl>
              <input value={form.sales_order_no} onChange={async e => {
                set("sales_order_no", e.target.value);
                if (e.target.value.length > 5) {
                  try {
                    const res = await fetch(`${API}/sales-orders`, { headers: hdr() });
                    if (res.ok) {
                      const data = await res.json();
                      const so = data.find(s => s.order_no === e.target.value);
                      if (so) {
                        if (so.customer_name) set("customer", so.customer_name);
                        const woRes = await fetch(`${API}/work-orders`, { headers: hdr() });
                        if (woRes.ok) {
                          const woData = await woRes.json();
                          const wo = woData.find(w => w.sales_order_no === e.target.value);
                          set("work_order_no", wo ? wo.order_no : "—");
                          if (wo?.product_code) set("product_code", wo.product_code);
                          if (wo?.product_name) set("product_name", wo.product_name);
                        }
                      }
                    }
                  } catch(e) {}
                }
              }} placeholder="YI-SO-2026-0001" style={inp} />
            </div>
            <div style={{ position: "relative" }}><Lbl>{n.customer}</Lbl>
              <input value={form.customer} onChange={async e => {
                set("customer", e.target.value);
                if (e.target.value.length > 2) {
                  try {
                    const res = await fetch(`${API}/customers?search=${e.target.value}`, { headers: hdr() });
                    if (res.ok) {
                      const data = await res.json();
                      set("_customerSuggestions", data.slice(0, 5));
                    }
                  } catch(e) {}
                } else {
                  set("_customerSuggestions", []);
                }
              }} placeholder={n.customerPlaceholder} style={inp} />
              {(form._customerSuggestions || []).length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: t.bg.modal, border: `1px solid ${t.border.input}`, borderRadius: 8, zIndex: 100, maxHeight: 160, overflowY: "auto", boxShadow: t.shadow.modal }}>
                  {form._customerSuggestions.map((c, i) => (
                    <div key={i} onClick={() => { set("customer", c.customer_name || c.name || c.company_name || ""); set("_customerSuggestions", []); }}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, color: t.text.primary, borderBottom: `1px solid ${t.border.subtle}` }}
                      onMouseEnter={e => e.currentTarget.style.background = t.bg.toggle}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}>
                      {c.customer_name || c.name || c.company_name || ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <Sec color="#f97316">⚠️ {n.nonconformityDetails}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{n.severity} *</Lbl>
              <select value={form.severity} onChange={e => set("severity", e.target.value)} style={{ ...sel, color: SEVERITY_COLORS[form.severity], fontWeight: 700 }}>
                <option value="minor">{n.severityMinorOption}</option>
                <option value="major">{n.severityMajorOption}</option>
                <option value="critical">{n.severityCriticalOption}</option>
              </select>
            </div>
            <div><Lbl>{n.category}</Lbl>
              <select value={form.ncr_category} onChange={e => set("ncr_category", e.target.value)} style={sel}>
                {Object.entries(getCategoryLabels()).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><Lbl>{n.affectedQuantity}</Lbl><input type="number" min="0" value={form.quantity_affected} onChange={e => set("quantity_affected", e.target.value)} placeholder="0" style={inp} /></div>
            <div><Lbl>{n.costImpact} ({getCurrencySymbol()})</Lbl><input type="number" min="0" step="0.01" value={form.cost_impact} onChange={e => set("cost_impact", e.target.value)} placeholder="0.00" style={inp} /></div>
          </div>
          <div><Lbl>{n.nonconformityDescription} *</Lbl>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder={n.nonconformityPlaceholder} rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <Sec color="#60a5fa">🔍 {n.analysisDecision}</Sec>
          <div><Lbl>{n.rootCauseAnalysis}</Lbl>
            <textarea value={form.root_cause} onChange={e => set("root_cause", e.target.value)} placeholder={n.rootCausePlaceholder} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div><Lbl>{n.immediateAction}</Lbl>
            <textarea value={form.immediate_action} onChange={e => set("immediate_action", e.target.value)} placeholder={n.immediateActionPlaceholder} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>

          <div><Lbl>{n.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {form.severity === "critical" && (
            <div style={{ padding: "12px 16px", background: "#ef444411", border: "1px solid #ef444433", borderRadius: 10, fontSize: 12, color: "#ef4444" }}>
              🚨 <strong>{n.criticalNcrTitle}</strong> — {n.criticalNcrInfo}
            </div>
          )}
          {form.severity === "major" && (
            <div style={{ padding: "12px 16px", background: "#f9731611", border: "1px solid #f9731633", borderRadius: 10, fontSize: 12, color: "#f97316" }}>
              ⚠️ <strong>{n.majorNcrTitle}</strong> — {n.majorNcrInfo}
            </div>
          )}
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{n.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading
      ? (n.saving)
      : (
          record
            ? `💾 ${n.update}`
            : `🔴 ${n.createNcr}`
        )
    }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CAPA FORM MODAL ──────────────────────────────────────────────────────
const CAPAFormModal = ({ ncr, record, onClose, onSaved, t }) => {
  const { t: langPack } = useLanguage();
  const n = langPack.ncrPage || {};

  const empty = {
    ncr_id: ncr?.id || null, capa_type: "corrective", title: "",
    problem_statement: "", root_cause_analysis: "", root_cause_method: "5why",
    action_plan: "", responsible_person: "", responsible_department: "",
    target_date: "", priority: "medium", cost_of_action: "", notes: "",
  };
  const [form, setForm] = useState(record ? { ...empty, ...record } : empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title) return setError(n.capaTitleRequired);
    if (!form.target_date) return setError(n.capaTargetDateRequired);
    setLoading(true); setError("");
    try {
      const url = record ? `${API}/capa/${record.id}` : `${API}/capa`;
      const method = record ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || n.capaSaveFailed);
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const Sec = ({ children, color = "#60a5fa" }) => <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4, paddingTop: 8, borderTop: `1px solid ${t.border.subtle}` }}>{children}</div>;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1001, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,#60a5fa22,#a78bfa11)", borderBottom: "1px solid #60a5fa33", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{record ? `✏️ ${n.editCapa}` : `🔵 ${n.newCapa}`}</div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
              {ncr ? `NCR: ${ncr.ncr_no} — ${ncr.product_code}` : (n.capaSubtitle)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Lbl>{n.capaType}</Lbl>
              <select value={form.capa_type} onChange={e => set("capa_type", e.target.value)} style={sel}>
                <option value="corrective">{n.capaCorrectiveOption}</option>
                <option value="preventive">{n.capaPreventiveOption}</option>
              </select>
            </div>
            <div><Lbl>{n.priority}</Lbl>
              <select value={form.priority} onChange={e => set("priority", e.target.value)} style={{ ...sel, color: PRIORITY_COLORS[form.priority], fontWeight: 700 }}>
                {Object.entries(getPriorityLabels()).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div><Lbl>{n.title} *</Lbl><input value={form.title} onChange={e => set("title", e.target.value)} placeholder={n.capaTitlePlaceholder} style={inp} /></div>
          <div><Lbl>{n.problemStatement}</Lbl><textarea value={form.problem_statement} onChange={e => set("problem_statement", e.target.value)} placeholder={n.problemStatementPlaceholder} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          <Sec color="#a78bfa">🔍 {n.rootCauseAnalysis}</Sec>
          <div><Lbl>{n.analysisMethod}</Lbl>
            <select value={form.root_cause_method} onChange={e => set("root_cause_method", e.target.value)} style={sel}>
              {Object.entries({
      "5why": n.rootMethod5Why,
      fishbone: n.rootMethodFishbone,
      pareto: n.rootMethodPareto,
      fmea: n.rootMethodFmea,
      other: n.rootMethodOther,
    }).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><Lbl>{n.rootCause}</Lbl><textarea value={form.root_cause_analysis} onChange={e => set("root_cause_analysis", e.target.value)} placeholder={n.capaRootCausePlaceholder} rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          <Sec color="#22c55e">✅ {n.actionPlan}</Sec>
          <div><Lbl>{n.actionPlan}</Lbl><textarea value={form.action_plan} onChange={e => set("action_plan", e.target.value)} placeholder={n.actionPlanPlaceholder} rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{n.responsiblePerson}</Lbl><input value={form.responsible_person} onChange={e => set("responsible_person", e.target.value)} placeholder={n.responsiblePersonPlaceholder} style={inp} /></div>
            <div><Lbl>{n.department}</Lbl><input value={form.responsible_department} onChange={e => set("responsible_department", e.target.value)} placeholder={n.department} style={inp} /></div>
            <div><Lbl>{n.targetDate} *</Lbl><input type="date" value={form.target_date} onChange={e => set("target_date", e.target.value)} style={inp} /></div>
          </div>
          <div><Lbl>{n.actionCost} ({getCurrencySymbol()})</Lbl><input type="number" min="0" step="0.01" value={form.cost_of_action} onChange={e => set("cost_of_action", e.target.value)} placeholder="0.00" style={inp} /></div>
          <div><Lbl>{n.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{n.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#60a5fa,#a78bfa)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading
      ? (n.saving)
      : (record
          ? `💾 ${n.update}`
          : `🔵 ${n.createCapa}`
        )
    }
          </button>
        </div>
      </div>
    </div>
  );
};

const CostForm = ({ ncr, t, onSaved }) => {
  const n = getLang().ncrPage || {};
  const [form, setForm] = useState({
    internal_cost: ncr.internal_cost || 0,
    external_cost: ncr.external_cost || 0,
    rework_cost: ncr.rework_cost || 0,
    cost_impact: ncr.cost_impact || 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const total = Object.values(form).reduce((a, b) => a + parseFloat(b || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/ncr/${ncr.id}/costs`, { method: "PUT", headers: hdr(), body: JSON.stringify(form) });
      onSaved();
      
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const inp = { width: "100%", padding: "8px 10px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 7, color: t.text.primary, fontSize: 12, outline: "none", boxSizing: "border-box" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 }}>{children}</label>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 10 }}>
        <div>
          <Lbl>{n.costInternal} ({getCurrencySymbol()})</Lbl>
          <input type="number" min="0" step="0.01" value={form.internal_cost} onChange={e => set("internal_cost", e.target.value)} style={inp} />
          <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>{n.costInternalHint}</div>
        </div>
        <div>
          <Lbl>{n.costExternal} ({getCurrencySymbol()})</Lbl>
          <input type="number" min="0" step="0.01" value={form.external_cost} onChange={e => set("external_cost", e.target.value)} style={inp} />
          <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>{n.costExternalHint}</div>
        </div>
        <div>
          <Lbl>{n.costRework} ({getCurrencySymbol()})</Lbl>
          <input type="number" min="0" step="0.01" value={form.rework_cost} onChange={e => set("rework_cost", e.target.value)} style={inp} />
          <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>{n.costReworkHint}</div>
        </div>
        <div>
          <Lbl>{n.costOtherImpact} ({getCurrencySymbol()})</Lbl>
          <input type="number" min="0" step="0.01" value={form.cost_impact} onChange={e => set("cost_impact", e.target.value)} style={inp} />
          <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>{n.costOtherImpactHint}</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>
          {n.total}: {formatMoney(total)}
          {ncr.recurrence_count > 0 && <span style={{ marginLeft: 10, fontSize: 11, color: "#f97316" }}>⚠️ {(n.recurrenceWarning).replace("{count}", ncr.recurrence_count + 1)}</span>}
        </div>
        <button onClick={handleSave} disabled={saving} style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: saving ? t.bg.tag : "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
          {saving ? (n.saving) : `💾 ${n.saveCost}`}
        </button>
      </div>
    </div>
  );
};
// ─── NCR DETAY MODAL ──────────────────────────────────────────────────────
const NCRDetailModal = ({ ncr: initialNcr, onClose, onRefresh, t }) => {
  const { t: langPack } = useLanguage();
  const n = langPack.ncrPage || {};
  const [data, setData] = useState({ ncr: initialNcr, capas: [] });
  const [loading, setLoading] = useState(true);
  const [showCAPAForm, setShowCAPAForm] = useState(false);
  const [editCapa, setEditCapa] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveForm, setApproveForm] = useState({ disposition: initialNcr.disposition || "pending", notes: "" });

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ncr/${initialNcr.id}`, { headers: hdr() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [initialNcr.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await fetch(`${API}/ncr/${initialNcr.id}/approve`, { method: "PATCH", headers: hdr(), body: JSON.stringify({ action: "approve", ...approveForm, scrap_value: approveForm.scrap_value || 0 }) });
      if (!res.ok) throw new Error((await res.json()).detail || n.genericError);
      fetchDetail(); onRefresh(); onClose();
    } catch (e) { alert(e.message); }
    setApproving(false);
  };

  const handleClose = async () => {
    if (!window.confirm(n.closeConfirm)) return;
    await fetch(`${API}/ncr/${initialNcr.id}/close`, { method: "PATCH", headers: hdr(), body: JSON.stringify({ notes: n.closedNote }) });
    fetchDetail(); onRefresh();
  };

  const ncr = data.ncr;
  const capas = data.capas || [];
  const sevColor = SEVERITY_COLORS[ncr.severity] || "#6b7280";
  const statColor = STATUS_COLORS[ncr.status] || "#6b7280";
  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 860, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: `linear-gradient(135deg,${sevColor}22,${sevColor}08)`, borderBottom: `1px solid ${sevColor}33`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text.primary }}>{ncr.ncr_no}</div>
              <Badge label={getSeverityLabels()[ncr.severity] || ncr.severity} color={sevColor} />
              <Badge label={getStatusLabels()[ncr.status] || ncr.status} color={statColor} />
              {ncr.source_type === "customer_complaint" && <Badge label={n.returnTitle} color="#f97316" />}
            </div>
            <div style={{ fontSize: 13, color: t.text.muted, marginTop: 4 }}>{ncr.product_code} {ncr.product_name ? `— ${ncr.product_name}` : ""} · {fmt(ncr.detection_date)}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: 12 }}>
              <div style={{ width: 24, height: 24, border: `2px solid ${t.border.default}`, borderTopColor: "#ef4444", borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            </div>
          ) : (
            <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20 }}>
              {/* KPI */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                {[
                  { label: n.affectedQuantity, value: `${ncr.quantity_affected || 0} ${n.piece}`, color: "#f97316" },
                  { label: n.category, value: getCategoryLabels()[ncr.ncr_category] || ncr.ncr_category, color: t.accent.blue },
                  { label: n.costImpact, value: money(ncr.cost_impact), color: "#ef4444" },
                  { label: n.capaCount, value: `${capas.length} ${n.piece}`, color: "#a78bfa" },
                ].map(card => (
                  <div key={card.label} style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4 }}>{card.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: card.color }}>{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Açıklama */}
              <div style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>{n.nonconformityDescription}</div>
                <div style={{ fontSize: 13, color: t.text.primary, lineHeight: 1.6 }}>{ncr.description || "—"}</div>
              </div>

              {/* Kök Neden & Acil Önlem */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>🔍 {n.rootCause}</div>
                  <div style={{ fontSize: 13, color: t.text.secondary, lineHeight: 1.6 }}>{ncr.root_cause || n.notEnteredYet}</div>
                </div>
                <div style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>⚡ {n.immediateAction}</div>
                  <div style={{ fontSize: 13, color: t.text.secondary, lineHeight: 1.6 }}>{ncr.immediate_action || n.notEnteredYet}</div>
                </div>
              </div>

              {/* Onay Zinciri */}
              <div style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>✅ {n.approvalChain}</div>
                <div style={{ display: "flex", gap: 0 }}>
                  {[
                    { label: n.qualityManager, approved: ncr.quality_manager_id, by: ncr.quality_manager_id, at: ncr.quality_manager_approved_at, required: true },
                    { label: n.productionManager, approved: ncr.production_manager_id, by: ncr.production_manager_id, at: ncr.production_manager_approved_at, required: ncr.severity !== "minor" },
                    { label: n.generalManager, approved: ncr.general_manager_id, by: ncr.general_manager_id, at: ncr.general_manager_approved_at, required: ncr.severity === "critical" },
                  ].map((step, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center", padding: "0 8px" }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", margin: "0 auto 8px", background: step.approved ? "#22c55e18" : step.required ? "#f9731618" : t.bg.tag, border: `2px solid ${step.approved ? "#22c55e" : step.required ? "#f97316" : t.border.subtle}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                        {step.approved ? "✅" : step.required ? "⏳" : "➖"}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: t.text.primary }}>{step.label}</div>
                      {step.approved
                        ? <div style={{ fontSize: 10, color: "#22c55e", marginTop: 2 }}>{step.by} · {fmt(step.at)}</div>
                        : <div style={{ fontSize: 10, color: step.required ? "#f97316" : t.text.muted, marginTop: 2 }}>{step.required
                            ? (n.pending)
                            : (n.notRequired)}</div>
                      }
                    </div>
                  ))}
                </div>
              </div>

              {/* Onay Formu */}
                {!["closed", "cancelled", "approved"].includes(ncr.status) && (
                <div style={{ background: "#60a5fa11", border: "1px solid #60a5fa33", borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.accent.blue, marginBottom: 12 }}>📝 {n.approvalDecision}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{n.decision}</label>
                      <select value={approveForm.disposition} onChange={e => setApproveForm(p => ({ ...p, disposition: e.target.value }))} style={sel}>
                        {Object.entries(getDispositionLabels()).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{n.note}</label>
                      <input value={approveForm.notes} onChange={e => setApproveForm(p => ({ ...p, notes: e.target.value }))} placeholder={n.approvalNotePlaceholder} style={inp} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={handleApprove} disabled={approving} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                      {approving
                        ? (n.processing)
                        : `✅ ${n.approveApply}`
                      }
                    </button>
                    {ncr.status === "approved" && (
                      <button onClick={handleClose} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#6b7280,#4b5563)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                        🔒 {n.closeNcr}
                      </button>
                    )}
                  </div>
                  {approveForm.disposition === "re_manufacture" && <div style={{ marginTop: 10, padding: "10px 14px", background: "#a78bfa11", border: "1px solid #a78bfa33", borderRadius: 8, fontSize: 12, color: "#a78bfa" }}>🏭 <strong>{getDispositionLabels().re_manufacture}</strong> — {n.remanufactureInfo}</div>}
                  {approveForm.disposition === "rework" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#60a5fa11", border: "1px solid #60a5fa33", borderRadius: 8, fontSize: 12, color: "#60a5fa" }}>
                        🔧 <strong>{getDispositionLabels().rework}</strong> — {n.reworkInfo}
                      </div>
                    )}
                    {approveForm.disposition === "replacement" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#f59e0b11", border: "1px solid #f59e0b33", borderRadius: 8, fontSize: 12, color: "#f59e0b" }}>
                        🔄 <strong>{getDispositionLabels().replacement}</strong> — {n.replacementInfo}
                      </div>
                    )}
                    {approveForm.disposition === "return_supplier" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#f9731611", border: "1px solid #f9731633", borderRadius: 8, fontSize: 12, color: "#f97316" }}>
                        📦 <strong>{getDispositionLabels().return_supplier}</strong> — {n.returnSupplierInfo}
                      </div>
                    )}
                  {approveForm.disposition === "scrap" && <div style={{ marginTop: 10, padding: "10px 14px", background: "#ef444411", border: "1px solid #ef444433", borderRadius: 8, fontSize: 12, color: "#ef4444" }}>🗑️ <strong>{getDispositionLabels().scrap}</strong> — {n.scrapInfo}</div>}
                  {approveForm.disposition === "scrap" && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#ef4444", textTransform:"uppercase", marginBottom:6 }}>{n.scrapRevenue} ({getCurrencySymbol()})</label>
                    <input type="number" min="0" step="0.01"
                      value={approveForm.scrap_value || ""}
                      onChange={e => setApproveForm(p => ({...p, scrap_value: e.target.value}))}
                      placeholder={n.scrapRevenuePlaceholder}
                      style={{ width:"100%", padding:"9px 12px", background:t.bg.input, border:"1px solid #ef444444", borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }} />
                  </div>
                )}
                </div>
              )}

              {/* CAPA Listesi */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>🔵 {n.capaRecords} ({capas.length})</div>
                  <button onClick={() => { setEditCapa(null); setShowCAPAForm(true); }} style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#60a5fa,#a78bfa)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>+ {n.addCapa}</button>
                </div>
                {capas.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", background: t.bg.toggle, borderRadius: 12, color: t.text.placeholder, fontSize: 13 }}>
                    {n.noCapaForNcr}
                  </div>
                ) : capas.map((ca) => (
                  <div key={ca.id} style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: t.text.muted }}>{ca.capa_no}</span>
                          <Badge label={ca.capa_type === "corrective"
                              ? (n.corrective)
                              : (n.preventive)} color={ca.capa_type === "corrective" ? "#60a5fa" : "#a78bfa"} />
                          <Badge label={getCapaStatusLabels()[ca.status] || ca.status} color={CAPA_STATUS_COLORS[ca.status] || "#6b7280"} />
                          <Badge label={getPriorityLabels()[ca.priority] || ca.priority} color={PRIORITY_COLORS[ca.priority] || "#6b7280"} />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 4 }}>{ca.title}</div>
                        <div style={{ fontSize: 12, color: t.text.muted }}>{n.responsible}: <strong>{ca.responsible_person || "—"}</strong> · {n.target}: <strong>{fmt(ca.target_date)}</strong></div>
                        {ca.action_plan && <div style={{ fontSize: 12, color: t.text.secondary, marginTop: 6 }}>{ca.action_plan.slice(0, 150)}{ca.action_plan.length > 150 ? "..." : ""}</div>}
                      </div>
                      <button onClick={() => { setEditCapa(ca); setShowCAPAForm(true); }} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.card, color: t.text.secondary, cursor: "pointer", fontSize: 11, marginLeft: 12 }}>✏️</button>
                    </div>
                    {ca.status !== "closed" && (
                      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {["in_progress", "completed", "closed"].map(s => (
                          <button key={s} onClick={async () => {
                            await fetch(`${API}/capa/${ca.id}`, { method: "PUT", headers: hdr(), body: JSON.stringify({ ...ca, status: s }) });
                            fetchDetail();
                          }} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${CAPA_STATUS_COLORS[s]}44`, background: `${CAPA_STATUS_COLORS[s]}11`, color: CAPA_STATUS_COLORS[s], cursor: "pointer", fontSize: 10, fontWeight: 700 }}>
                            {getCapaStatusLabels()[s]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* MALİYET GİRİŞİ */}
                <div style={{ background: "#ef444411", border: "1px solid #ef444433", borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", marginBottom: 12 }}>💸 {n.qualityCostEntry}</div>
                  <CostForm ncr={ncr} t={t} onSaved={fetchDetail} />
                </div>

              {ncr.notes && (
                <div style={{ background: t.bg.toggle, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>📝 {n.approvalHistory}</div>
                  <pre style={{ fontSize: 12, color: t.text.secondary, whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>{ncr.notes}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{n.close}</button>
        </div>
      </div>

      {showCAPAForm && (
        <CAPAFormModal ncr={ncr} record={editCapa} t={t}
          onClose={() => { setShowCAPAForm(false); setEditCapa(null); }}
          onSaved={() => { setShowCAPAForm(false); setEditCapa(null); fetchDetail(); }} />
      )}
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function NCR() {
  const { t, themeName } = useTheme();
  useLanguage();
  const n = getLang().ncrPage || {};
  const [mainTab, setMainTab] = useState(() => localStorage.getItem("ncr_tab") || "list");
  const [ncrList, setNcrList] = useState([]);
  const [capaList, setCapaList] = useState([]);
  const [returnList, setReturnList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [returnPage, setReturnPage] = useState(1);
  const [returnPageSize, setReturnPageSize] = useState(25);
  const [capaPage, setCapaPage] = useState(1);
  const [capaPageSize, setCapaPageSize] = useState(25);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [showNCRForm, setShowNCRForm] = useState(false);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [editNCR, setEditNCR] = useState(null);
  const [detailNCR, setDetailNCR] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/ncr`, { headers: hdr() }),
        fetch(`${API}/capa`, { headers: hdr() }),
        fetch(`${API}/ncr/summary`, { headers: hdr() }),
        fetch(`${API}/customer-returns`, { headers: hdr() }),
      ]);
      if (r1.ok) setNcrList(await r1.json());
      if (r2.ok) setCapaList(await r2.json());
      if (r3.ok) setSummary(await r3.json());
      if (r4.ok) setReturnList(await r4.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { localStorage.setItem("ncr_tab", mainTab); }, [mainTab]);

  useEffect(() => {
    setPage(1);
    setReturnPage(1);
    setCapaPage(1);
  }, [search, filterStatus, filterSeverity, mainTab]);

  const filtered = ncrList.filter(r => {
    const q = search.toLowerCase();
    return (!q || r.ncr_no?.toLowerCase().includes(q) || r.product_code?.toLowerCase().includes(q) || r.customer?.toLowerCase().includes(q))
      && (!filterStatus || r.status === filterStatus)
      && (!filterSeverity || r.severity === filterSeverity);
  });

  const pagedNcr = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const s = summary?.summary || {};
  const pagedReturns = returnList.slice(
    (returnPage - 1) * returnPageSize,
    returnPage * returnPageSize
  );

  const pagedCapa = capaList.slice(
    (capaPage - 1) * capaPageSize,
    capaPage * capaPageSize
  );

  const tabs = [
    ["list", `🔴 ${n.listTab}`],
    ["returns", `📦 ${n.returnsTab}`],
    ["capa", "🔵 CAPA"],
    ["analytics", `📊 ${n.analyticsTab}`],
  ];
  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
  const tdBg = (i) => ({ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row });

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>🔴 NCR & CAPA</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>{n.pageSubtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
            {mainTab === "list" && <button onClick={() => { setEditNCR(null); setShowNCRForm(true); }} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>🔴 {n.createNcr}</button>}
            {mainTab === "returns" && <button onClick={() => setShowReturnForm(true)} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{`📦 ${n.addReturnRecord}`}</button>}
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 24 }}>
          <KpiCard label={n.totalNcr} value={s.total || 0} icon="📋" color={t.accent.blue} t={t} />
          <KpiCard label={n.open} value={s.open_count || 0} icon="🔴" color="#ef4444" t={t} />
          <KpiCard label={n.underReview} value={s.review_count || 0} icon="🔍" color="#f97316" t={t} />
          <KpiCard label={n.pendingApproval} value={s.pending_count || 0} icon="⏳" color="#eab308" t={t} />
          <KpiCard label={n.critical} value={s.critical_count || 0} icon="🚨" color="#ef4444" sub={`${s.major_count || 0} ${n.severityMajor}`} t={t} />
          <KpiCard label={n.totalCost} value={money(s.total_cost)} icon="💸" color="#ef4444" t={t} />
        </div>

        {/* Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {tabs.map(([v, label]) => (
            <button key={v} onClick={() => setMainTab(v)} style={{ padding: "9px 16px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: "#ef4444", borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{n.loading}</span>
          </div>
        ) : (
          <>
            {/* NCR LİSTESİ */}
            {mainTab === "list" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`🔍 ${n.searchPlaceholder}`}
                    style={{ flex: 1, minWidth: 200, padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none" }} />
                  <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
                    <option value="">{n.allSeverities}</option>
                    {Object.entries(getSeverityLabels()).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
                    <option value="">{n.allStatuses}</option>
                    {Object.entries(getStatusLabels()).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{n.ncrRecords}</div>
                    <span style={{ fontSize: 12, color: t.text.muted }}>{filtered.length} {n.records}</span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                      <thead><tr style={{ background: t.bg.theadRow }}>
                        {[
                            "NCR No",
                            n.product,
                            n.customer,
                            n.date,
                            n.severity,
                            n.category,
                            n.decision,
                            "CAPA",
                            n.status,
                            ""
                          ].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={10} style={{ padding: 48, textAlign: "center" }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>🔴</div>
                            <div style={{ color: t.text.placeholder, fontSize: 14 }}>{n.noNcrRecords}</div>
                            <button onClick={() => setShowNCRForm(true)} style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {n.createFirstNcr}</button>
                          </td></tr>
                        ) : pagedNcr.map((r, i) => (
                          <tr key={r.id} onClick={() => setDetailNCR(r)} style={{ ...tdBg(i), cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = t.bg.rowHover}
                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? t.bg.rowAlt : t.bg.row}>
                            <td style={{ padding: "11px 12px" }}>
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#ef4444" }}>{r.ncr_no}</div>
                              <div style={{ fontSize: 10, color: t.text.muted }}>{r.work_order_no || "—"}</div>
                            </td>
                            <td style={{ padding: "11px 12px" }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{r.product_code}</div>
                              <div style={{ fontSize: 10, color: t.text.muted }}>{r.quantity_affected} {n.piece}</div>
                            </td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.secondary }}>{r.customer || "—"}</td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.muted }}>{fmt(r.detection_date)}</td>
                            <td style={{ padding: "11px 12px" }}><Badge label={getSeverityLabels()[r.severity] || r.severity} color={SEVERITY_COLORS[r.severity] || "#6b7280"} /></td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.secondary }}>{getCategoryLabels()[r.ncr_category] || r.ncr_category}</td>
                            <td style={{ padding: "11px 12px" }}><Badge label={getDispositionLabels()[r.disposition] || r.disposition} color={DISPOSITION_COLORS[r.disposition] || "#6b7280"} /></td>
                            <td style={{ padding: "11px 12px" }}>
                              <div style={{ fontSize: 12, color: r.capa_count > 0 ? "#60a5fa" : t.text.muted, fontWeight: r.capa_count > 0 ? 700 : 400 }}>
                                {r.capa_count > 0 ? `${r.capa_closed}/${r.capa_count}` : "—"}
                              </div>
                            </td>
                            <td style={{ padding: "11px 12px" }}><Badge label={getStatusLabels()[r.status] || r.status} color={STATUS_COLORS[r.status] || "#6b7280"} /></td>
                            <td style={{ padding: "11px 12px" }} onClick={e => e.stopPropagation()}>
                              <button onClick={() => { setEditNCR(r); setShowNCRForm(true); }} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 11 }}>✏️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* MÜŞTERİ İADESİ */}
            {mainTab === "list" && filtered.length > 0 && (
              <PaginationBar
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalRecords={filtered.length}
                label="NCR"
                t={t}
              />
            )}

            {mainTab === "returns" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ padding: "14px 20px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 12, fontSize: 13, color: t.accent.blue }}>
                  📦 {n.returnInfo}
                </div>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>📦 {n.returnRecords}</div>
                    <span style={{ fontSize: 12, color: t.text.muted }}>{returnList.length} {n.records}</span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: t.bg.theadRow }}>
                      {[
                        "NCR No",
                        n.product,
                        n.customer,
                        n.quantity,
                        n.severity,
                        n.date,
                        n.status,
                      ].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {returnList.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{n.noReturnRecords}</td></tr>
                      ) : pagedReturns.map((r, i) => (
                        <tr key={r.id} onClick={() => setDetailNCR(r)} style={{ ...tdBg(i), cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = t.bg.rowHover}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? t.bg.rowAlt : t.bg.row}>
                          <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 800, color: "#ef4444" }}>{r.ncr_no}</td>
                          <td style={{ padding: "11px 12px" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{r.product_code}</div>
                            <div style={{ fontSize: 10, color: t.text.muted }}>{r.product_name}</div>
                          </td>
                          <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.secondary }}>{r.customer || "—"}</td>
                          <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 700, color: "#f97316" }}>{r.quantity_affected} {n.piece}</td>
                          <td style={{ padding: "11px 12px" }}><Badge label={getSeverityLabels()[r.severity] || r.severity} color={SEVERITY_COLORS[r.severity] || "#6b7280"} /></td>
                          <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.muted }}>{fmt(r.detection_date)}</td>
                          <td style={{ padding: "11px 12px" }}><Badge label={getStatusLabels()[r.status] || r.status} color={STATUS_COLORS[r.status] || "#6b7280"} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CAPA */}
            {mainTab === "returns" && returnList.length > 0 && (
              <PaginationBar
                page={returnPage}
                setPage={setReturnPage}
                pageSize={returnPageSize}
                setPageSize={setReturnPageSize}
                totalRecords={returnList.length}
                label={n.returnPaginationLabel}
                t={t}
              />
            )}

            {mainTab === "capa" && (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>🔵 {n.capaRecords}</div>
                  <span style={{ fontSize: 12, color: t.text.muted }}>{capaList.length} {n.records}</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                    <thead><tr style={{ background: t.bg.theadRow }}>
                      {[
                        "CAPA No",
                        "NCR",
                        n.title,
                        n.type,
                        n.responsible,
                        n.targetDate,
                        n.progress,
                        n.priority,
                        n.status
                      ].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {capaList.length === 0 ? (
                        <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{n.noCapaRecords}</td></tr>
                      ) : pagedCapa.map((ca, i) => (
                        <tr key={ca.id} style={tdBg(i)}>
                          <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 800, color: "#60a5fa" }}>{ca.capa_no}</td>
                          <td style={{ padding: "11px 12px", fontSize: 11, color: "#ef4444" }}>{ca.ncr_no || "—"}</td>
                          <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ca.title}</td>
                          <td style={{ padding: "11px 12px" }}><Badge
                            label={ca.capa_type === "corrective"
                              ? (n.corrective)
                              : (n.preventive)}
                            color={ca.capa_type === "corrective" ? "#60a5fa" : "#a78bfa"}
                          /></td>
                          <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.secondary }}>{ca.responsible_person || "—"}</td>
                          <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.muted }}>{fmt(ca.target_date)}</td>
                          <td style={{ padding: "11px 12px" }}>
                            {ca.action_count > 0 ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: t.bg.tag, borderRadius: 3, overflow: "hidden", minWidth: 60 }}>
                                  <div style={{ width: `${(ca.action_done / ca.action_count) * 100}%`, height: "100%", background: "#22c55e", borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 11, color: t.text.muted }}>{ca.action_done}/{ca.action_count}</span>
                              </div>
                            ) : <span style={{ fontSize: 11, color: t.text.muted }}>—</span>}
                          </td>
                          <td style={{ padding: "11px 12px" }}><Badge label={getPriorityLabels()[ca.priority] || ca.priority} color={PRIORITY_COLORS[ca.priority] || "#6b7280"} /></td>
                          <td style={{ padding: "11px 12px" }}><Badge label={getCapaStatusLabels()[ca.status] || ca.status} color={CAPA_STATUS_COLORS[ca.status] || "#6b7280"} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ANALİZ */}
            {mainTab === "capa" && capaList.length > 0 && (
              <PaginationBar
                page={capaPage}
                setPage={setCapaPage}
                pageSize={capaPageSize}
                setPageSize={setCapaPageSize}
                totalRecords={capaList.length}
                label="CAPA"
                t={t}
              />
            )}

            {mainTab === "analytics" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>⚠️ {n.severityDistribution}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {[
                        { label: n.severityMinor, count: s.minor_count || 0, color: "#22c55e" },
                        { label: n.severityMajor, count: s.major_count || 0, color: "#f97316" },
                        { label: n.severityCritical, count: s.critical_count || 0, color: "#ef4444" },
                      ].map(item => (
                        <div key={item.label}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: t.text.secondary }}>{item.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: item.color }}>{item.count}</span>
                          </div>
                          <div style={{ height: 8, background: t.bg.tag, borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${s.total > 0 ? (item.count / s.total) * 100 : 0}%`, height: "100%", background: item.color, borderRadius: 4 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>📊 {n.categoryDistribution}</div>
                    {(summary?.by_category || []).length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={summary.by_category} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                          <XAxis type="number" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                          <YAxis type="category" dataKey="ncr_category" stroke={t.chart.axis} tick={{ fontSize: 9, fill: t.chart.axis }} width={80} tickFormatter={v => getCategoryLabels()[v] || v} />
                          <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                          <Bar dataKey="count" fill="#ef4444" radius={[0,3,3,0]} name={n.count} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{n.noData}</div>}
                  </div>

                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>🎯 {n.decisionDistribution}</div>
                    {(summary?.by_disposition || []).length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={150}>
                          <PieChart>
                            <Pie data={summary.by_disposition} dataKey="count" nameKey="disposition" cx="50%" cy="50%" outerRadius={60} label={false}>
                              {summary.by_disposition.map((entry, i) => <Cell key={i} fill={DISPOSITION_COLORS[entry.disposition] || "#6b7280"} />)}
                            </Pie>
                            <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} formatter={(v, n, p) => [v, getDispositionLabels()[p.payload.disposition] || p.payload.disposition]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {summary.by_disposition.map((item, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <div style={{ width: 7, height: 7, borderRadius: "50%", background: DISPOSITION_COLORS[item.disposition] || "#6b7280" }} />
                                <span style={{ color: t.text.secondary }}>{getDispositionLabels()[item.disposition] || item.disposition}</span>
                              </div>
                              <span style={{ fontWeight: 700, color: t.text.primary }}>{item.count}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{n.noData}</div>}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
                  {[
                    { label: n.last30DaysNcr, value: s.last_30_days || 0, icon: "📅", color: "#f97316" },
                    { label: n.closedCount, value: s.closed_count || 0, icon: "✅", color: "#22c55e" },
                    { label: n.totalCost, value: money(s.total_cost), icon: "💸", color: "#ef4444" },
                    { label: n.totalCapa, value: capaList.length, icon: "🔵", color: "#60a5fa" },
                  ].map(card => (
                    <div key={card.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "20px 24px", boxShadow: t.shadow.card, textAlign: "center" }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{card.icon}</div>
                      <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{card.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showNCRForm && <NCRFormModal record={editNCR} t={t} onClose={() => { setShowNCRForm(false); setEditNCR(null); }} onSaved={() => { setShowNCRForm(false); setEditNCR(null); fetchData(); }} />}
      {showReturnForm && <CustomerReturnModal t={t} onClose={() => setShowReturnForm(false)} onSaved={() => { setShowReturnForm(false); fetchData(); }} />}
      {detailNCR && <NCRDetailModal ncr={detailNCR} t={t} onClose={() => setDetailNCR(null)} onRefresh={fetchData} />}
    </div>
  );
}
